<template>
    <div>
        <!-- Select Context -->
        <l-select
            :value="context"
            :options="optionsContext"
            style="margin-top: 15px"
            @change="e => showContext(e.target.value)"
        />
        <!--/Select Context -->

        <hr />

        <!-- Goals -->
        <div v-if="contextView === 'goals'">
            <l-reviews-update-context-panel-goals
                :goals="filteredRevieweeGoals"
                :reviewee-user="reviewCycle.revieweeUser"
            />
            <!-- KR edit modal -->
            <l-goal-k-r-edit-modal
                v-show="$route.query.editGoalId && $route.query.editKeyResultId"
                :goal="editGoal"
                @update="goalUpdated"
            ></l-goal-k-r-edit-modal>
            <!--/KR edit modal -->
        </div>
        <!--/Goals -->

        <!-- Feedback -->
        <div v-if="contextView === 'feedback'">
            <l-reviews-update-context-panel-feedback
                :reviewee-feedback="revieweeFeedback"
                :review-cycle="reviewCycle"
                :role="role"
                :view-as="viewAs"
                :view="view"
                :contributor-id="contributorId"
                :reviewee-id="revieweeId"
                :entrypoint="entrypoint"
            />
        </div>
        <!--/Feedback -->

        <!-- Past Reviews -->
        <div v-if="contextView === 'reviews'">
            <l-reviews-update-context-panel-reviews
                :reviews="filteredPastReviews"
                :review-cycle="reviewCycle"
            />
        </div>
        <!--/Past Reviews -->

        <!-- Guidelines -->
        <div v-if="contextView === 'guidelines'">
            <l-reviews-update-context-panel-guidelines :review-cycle="reviewCycle" />
        </div>
        <!--/Guidelines -->

        <!-- Timeline -->
        <div v-if="contextView === 'timeline'">
            <l-reviews-update-context-panel-timeline :review-cycle="reviewCycle" />
        </div>
        <!--/Timeline -->

        <!-- Meetings -->
        <div v-if="contextView === 'meetings'">
            <l-reviews-update-context-panel-meetings :review-cycle="reviewCycle" />
        </div>
        <!--/Meetings -->

        <!-- Development Goal -->
        <div
            v-show="
                user.rights.G_CREATE &&
                    reviewCycle.revieweeUser &&
                    reviewCycle.revieweeUser._id &&
                    contextView !== 'goals' &&
                    (viewAs === 'manager' || viewAs === 'reviewee') &&
                    mState.contributor >= 3
            "
        >
            <hr />
            <a class="btn btn-primary btn-block btn-raised" href="#/goals/create/" target="_blank">
                <i class="fal fa-seedling"></i>
                <span class="hidden-xs">
                    {{ $t(`goals.list.btnCreateDevGoal`) }}
                </span>
            </a>
        </div>
        <!--/Development Goal -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import { alphabetically } from "@/utils/sort-object";

import LSelect from "@/components/LSelect";
import LGoalKREditModal from "@/components/LGoalKREditModal";

import LReviewsUpdateContextPanelFeedback from "@/components/LReviewsUpdateContextPanelFeedback";
import LReviewsUpdateContextPanelGoals from "@/components/LReviewsUpdateContextPanelGoals";
import LReviewsUpdateContextPanelGuidelines from "@/components/LReviewsUpdateContextPanelGuidelines";
import LReviewsUpdateContextPanelMeetings from "@/components/LReviewsUpdateContextPanelMeetings";
import LReviewsUpdateContextPanelReviews from "@/components/LReviewsUpdateContextPanelReviews";
import LReviewsUpdateContextPanelTimeline from "@/components/LReviewsUpdateContextPanelTimeline";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LSelect,

        LReviewsUpdateContextPanelFeedback,
        LReviewsUpdateContextPanelGoals,
        LReviewsUpdateContextPanelGuidelines,
        LReviewsUpdateContextPanelMeetings,
        LReviewsUpdateContextPanelReviews,
        LReviewsUpdateContextPanelTimeline,
        LGoalKREditModal,
    },
    props: {
        reviewCycle: {
            type: Object,
            required: true,
        },
        revieweeId: {
            type: String,
            required: true,
        },
        role: {
            type: String,
            required: true,
        },
        contributorId: {
            type: String,
            required: true,
        },
        viewAs: {
            type: String,
            required: true,
        },
        view: {
            type: String,
            required: true,
        },
        entrypoint: {
            type: String,
            required: true,
        },
        mState: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            context: "",
            contextView: "guidelines",
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            revieweeGoals: "goals/goals/list",
            feedbackByUser: "feedback/feedback/listByUser",
            pastReviews: "reviews/assessments/past",
        }),
        optionsContext() {
            const options = [];

            options.push({
                value: "",
                text: this.$t(`reviews.update.menuExploreContext`),
            });

            if (this.reviewCycle?.revieweeUser?._id) {
                options.push({
                    value: "reviews",
                    text: this.$t(`reviews.update.menuReviews`, {
                        firstname: this.reviewCycle.revieweeUser.firstname,
                    }),
                });
            }

            if (this.user.rights.G_CREATE && this.reviewCycle?.revieweeUser?._id) {
                options.push({
                    value: "goals",
                    text: this.$t(`reviews.update.menuGoals`, {
                        firstname: this.reviewCycle.revieweeUser.firstname,
                    }),
                });
            }
            if (this.user.rights.F_VIEW && this.reviewCycle?.revieweeUser?._id) {
                options.push({
                    value: "feedback",
                    text: this.$t(`reviews.update.menuFeedback`, {
                        firstname: this.reviewCycle.revieweeUser.firstname,
                    }),
                });
            }
            if (
                this.user.rights.M_VIEW &&
                this.viewAs !== "reviewee" &&
                this.reviewCycle?.revieweeUser?._id
            ) {
                options.push({
                    value: "meetings",
                    text: this.$t(`reviews.update.menuMeetings`, {
                        firstname: this.reviewCycle.revieweeUser.firstname,
                    }),
                });
            }
            if (this.reviewCycle.description && this.reviewCycle.description !== "") {
                options.push({
                    value: "guidelines",
                    text: this.$t(`reviews.update.menuGuidelines`),
                });
            }

            if (this.reviewCycle.status && Object.keys(this.reviewCycle.status).length) {
                options.push({
                    value: "timeline",
                    text: this.$t(`reviews.update.menuNextSteps`),
                });
            }

            return options;
        },

        filteredRevieweeGoals() {
            return this.revieweeGoals
                .filter(goal => goal.status !== "archived")
                .sort(alphabetically("type", "desc"));
        },
        revieweeFeedback() {
            const revieweeFeedback = this.feedbackByUser[this.revieweeId] || [];

            return revieweeFeedback.map(feedback => ({
                ...feedback,
                thanksNoteReceivers:
                    feedback.thanksNoteReceivers &&
                    feedback.thanksNoteReceivers.map(receiver => ({
                        ...receiver,
                        picture:
                            (!receiver.picture ||
                                receiver.picture === "/img/icons/user_small.png") &&
                            receiver.name
                                ? teamPicture
                                : receiver.picture,
                    })),
            }));
        },
        filteredPastReviews() {
            return this.pastReviews.filter(pR => {
                return pR.reviewCycle !== this.reviewCycle._id;
            });
        },
        editGoal() {
            const goalId = this.$route.query.editGoalId;
            return this.filteredRevieweeGoals?.find(g => g._id === goalId) || {};
        },
    },
    methods: {
        goalUpdated() {
            // Goal progress calculation only happens on the server, so we need to reload the whole list
            // once the modal is closed.
            //
            this.fetchGoals();
        },
        showContext(context) {
            this.context = context;

            switch (context) {
                case "goals":
                    this.fetchGoals();
                    break;
                case "feedback":
                    this.fetchFeedback();
                    break;

                case "reviews":
                    this.fetchReviews();
                    break;

                case "": {
                    this.contextView = "guidelines";
                    break;
                }
                default: {
                    this.contextView = context;
                }
            }
        },
        fetchGoals() {
            var type;
            var users;

            if (this.user._id !== this.reviewCycle?.revieweeUser?._id) {
                type = "custom";
                users = { users: [this.revieweeId] };
            } else {
                type = "my";
                users = {};
            }

            this.$store
                .dispatch({
                    type: "goals/goals/getList",
                    payload: {
                        listSegment: type,
                        listCustomFilter: users,
                        output: "list",
                    },
                })
                .then(() => {
                    this.contextView = "goals";
                });
        },
        fetchFeedback() {
            this.$store
                .dispatch({
                    type: "feedback/feedback/getListByUser",
                    payload: {
                        userId: this.reviewCycle?.revieweeUser?._id,
                    },
                })
                .then(() => {
                    this.contextView = "feedback";
                });
        },
        fetchReviews() {
            this.$store
                .dispatch({
                    type: "reviews/assessments/getPast",
                    payload: { userId: this.reviewCycle?.revieweeUser?._id },
                })
                .then(() => {
                    this.contextView = "reviews";
                });
        },
    },
};
</script>
